import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, X } from 'lucide-react'
import { Button } from '../ui/button'
import {
  AML_CATEGORY_TABS,
  classifyCategoryBucket,
  formatThreatLevel,
  groupResultsByCategory,
} from '../../utils/amlResultCategories'
import { getMatchCardHeadline, getMatchCardSubtitle } from '../../utils/amlMatchPresentation'
import { sanitizeVendorBranding } from '../../utils/sanitizeVendorBranding'
import {
  getAmlRiskBadgeColor,
  getAmlConfidenceColor,
} from '../../utils/amlRiskColors'
import { classifyNameFit } from '../../utils/filterAmlSearchResults'
import AmlMediaSource from './AmlMediaSource'

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pep', label: 'PEP' },
  { key: 'sanctions', label: 'Sanctions' },
  { key: 'adverseMedia', label: 'Adverse Media' },
]

const NO_MATCH_PRESETS = [
  'Not enough evidence to connect these names to this patron',
  'Name similarity only — different person (DOB / location mismatch)',
  'Common name / false positive',
]

function dispositionFingerprint({ decision, reason, selectedKey }) {
  if (decision === 'no_match') return `no_match::${reason}`
  if (decision === 'confirmed_match') return `confirmed_match::${selectedKey || ''}::${reason}`
  return null
}

/**
 * Profile AML review: matches list on the left (filterable), detail on the right,
 * plus "None of these" with a required reason.
 *
 * Save / Confirm stays disabled after a successful save until the disposition changes
 * (different match, none-of-these toggle, or reason edit).
 */
export default function ProfileAmlMatchReviewPanel({
  results = [],
  searchName = '',
  checkId = null,
  initialDecision = null,
  initialReason = '',
  onSaveReview,
  saving = false,
}) {
  const grouped = useMemo(() => groupResultsByCategory(results), [results])
  const counts = useMemo(() => {
    const c = { all: results.length }
    for (const t of AML_CATEGORY_TABS) c[t.key] = grouped[t.key]?.length || 0
    return c
  }, [grouped, results])

  const [categoryFilter, setCategoryFilter] = useState('all')
  const [selectedKey, setSelectedKey] = useState(null)
  const [noneSelected, setNoneSelected] = useState(initialDecision === 'no_match')
  const [reason, setReason] = useState(initialReason || '')
  const [saveError, setSaveError] = useState(null)
  const [lastSavedFingerprint, setLastSavedFingerprint] = useState(null)
  const hydratedConfirmedRef = useRef(false)

  useEffect(() => {
    setNoneSelected(initialDecision === 'no_match')
    setReason(initialReason || '')
    setSaveError(null)
    hydratedConfirmedRef.current = false
    if (initialDecision === 'confirmed_match') setNoneSelected(false)
    if (initialDecision === 'no_match') {
      setLastSavedFingerprint(
        dispositionFingerprint({
          decision: 'no_match',
          reason: (initialReason || '').trim(),
          selectedKey: null,
        })
      )
    } else if (initialDecision === 'confirmed_match') {
      // Fingerprint filled once auto-select / selection settles (see effect below).
      setLastSavedFingerprint(null)
    } else {
      setLastSavedFingerprint(null)
    }
  }, [initialDecision, initialReason, checkId])

  const filtered = useMemo(() => {
    if (categoryFilter === 'all') return results
    return results.filter((m) => classifyCategoryBucket(m) === categoryFilter)
  }, [results, categoryFilter])

  const nameGroups = useMemo(() => {
    const exact = []
    const broader = []
    filtered.forEach((m, idx) => {
      const row = { m, idx }
      if (classifyNameFit(m, searchName) === 'exact') exact.push(row)
      else broader.push(row)
    })
    return { exact, broader }
  }, [filtered, searchName])

  const selected = useMemo(() => {
    if (noneSelected || selectedKey == null) return null
    return (
      filtered.find((m, i) => matchKey(m, i) === selectedKey) ||
      results.find((m, i) => matchKey(m, i) === selectedKey) ||
      null
    )
  }, [filtered, results, selectedKey, noneSelected])

  useEffect(() => {
    if (noneSelected) return
    if (selected) return
    if (filtered.length > 0) setSelectedKey(matchKey(filtered[0], 0))
  }, [filtered, categoryFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  // Treat an already-saved confirmed match as clean once we know which row is selected.
  useEffect(() => {
    if (initialDecision !== 'confirmed_match') return
    if (hydratedConfirmedRef.current) return
    if (!selectedKey || noneSelected) return
    hydratedConfirmedRef.current = true
    setLastSavedFingerprint(
      dispositionFingerprint({
        decision: 'confirmed_match',
        reason: (initialReason || '').trim(),
        selectedKey,
      })
    )
  }, [initialDecision, initialReason, selectedKey, noneSelected])

  const currentFingerprint = useMemo(() => {
    if (noneSelected) {
      return dispositionFingerprint({
        decision: 'no_match',
        reason: reason.trim(),
        selectedKey: null,
      })
    }
    if (selected && selectedKey) {
      return dispositionFingerprint({
        decision: 'confirmed_match',
        reason: reason.trim(),
        selectedKey,
      })
    }
    return null
  }, [noneSelected, selected, selectedKey, reason])

  const isDirty = Boolean(currentFingerprint) && currentFingerprint !== lastSavedFingerprint
  const noneReasonValid = reason.trim().length >= 8
  const canSaveNone = Boolean(checkId) && !saving && isDirty && noneReasonValid
  const canSaveConfirm = Boolean(checkId) && !saving && isDirty && Boolean(selected)

  async function saveConfirmed() {
    if (!selected || !onSaveReview || !canSaveConfirm) return
    setSaveError(null)
    const savedReason = reason.trim() || `Confirmed match: ${getMatchCardHeadline(selected)}`
    try {
      await onSaveReview({
        decision: 'confirmed_match',
        reason: savedReason,
        selected_match: selected,
      })
      setReason(savedReason)
      setLastSavedFingerprint(
        dispositionFingerprint({
          decision: 'confirmed_match',
          reason: savedReason,
          selectedKey,
        })
      )
    } catch (e) {
      setSaveError(e?.message || 'Failed to save')
    }
  }

  async function saveNone() {
    if (!onSaveReview || !canSaveNone) return
    const r = reason.trim()
    if (r.length < 8) {
      setSaveError('Enter a reason (at least 8 characters) explaining why none of these matches apply.')
      return
    }
    setSaveError(null)
    try {
      await onSaveReview({ decision: 'no_match', reason: r, selected_match: null })
      setLastSavedFingerprint(
        dispositionFingerprint({ decision: 'no_match', reason: r, selectedKey: null })
      )
    } catch (e) {
      setSaveError(e?.message || 'Failed to save')
    }
  }

  const savedBanner = !isDirty && lastSavedFingerprint ? (
    <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900 flex items-center gap-2">
      <Check className="h-4 w-4 shrink-0 text-green-700" />
      <span>
        Saved
        {lastSavedFingerprint.startsWith('no_match')
          ? ' — none of these match this patron'
          : ' — match confirmed'}
        . Change selection or reason to save again.
      </span>
    </div>
  ) : null

  return (
    <div className="flex flex-col gap-3 h-full min-h-0">
      <div className="flex flex-wrap gap-2 shrink-0">
        {FILTER_TABS.map((tab) => {
          const n = counts[tab.key] ?? 0
          if (tab.key !== 'all' && n === 0) return null
          const active = categoryFilter === tab.key
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => { setCategoryFilter(tab.key); setNoneSelected(false) }}
              className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                active
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded ${active ? 'bg-primary-500' : 'bg-gray-100'}`}>
                {n}
              </span>
            </button>
          )
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 flex-1 min-h-0">
        {/* Left: matches */}
        <div className="lg:col-span-2 flex flex-col min-h-0 border rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-700 shrink-0">
            Matches ({filtered.length}
            {searchName ? ` for “${searchName}”` : ''})
          </div>
          <div className="overflow-y-auto flex-1 p-2 space-y-3">
            <p className="text-[11px] text-gray-500 px-1">
              Exact match is the same names only. Two names match two names — a middle name or a similar name (Alex / Alexander) is a broader match.
            </p>
            <NameFitGroup
              title="Exact match"
              rows={nameGroups.exact}
              emptyMessage="No exact matches found"
              alwaysShow={filtered.length > 0}
              noneSelected={noneSelected}
              selectedKey={selectedKey}
              onSelect={(key) => { setNoneSelected(false); setSelectedKey(key); setSaveError(null) }}
            />
            <NameFitGroup
              title="Broader match"
              hint="Similar or extra names"
              rows={nameGroups.broader}
              emptyMessage="No broader matches found"
              alwaysShow={filtered.length > 0}
              noneSelected={noneSelected}
              selectedKey={selectedKey}
              onSelect={(key) => { setNoneSelected(false); setSelectedKey(key); setSaveError(null) }}
            />
            {filtered.length === 0 && (
              <div className="text-sm text-gray-500 p-3">No matches in this category.</div>
            )}
          </div>

          <div className="border-t p-2 shrink-0 bg-white">
            <button
              type="button"
              onClick={() => {
                setNoneSelected(true)
                setSelectedKey(null)
                setSaveError(null)
                if (!reason.trim()) setReason(NO_MATCH_PRESETS[0])
              }}
              className={`w-full text-left rounded-lg border p-3 transition-colors ${
                noneSelected
                  ? 'border-green-500 bg-green-50'
                  : 'border-dashed border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="font-semibold text-gray-900 flex items-center gap-2">
                <Check className={`h-4 w-4 ${noneSelected ? 'text-green-700' : 'text-gray-400'}`} />
                None of these match this patron
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Record that returned hits do not connect to this person (reason required).
              </div>
            </button>
          </div>
        </div>

        {/* Right: detail / none form */}
        <div className="lg:col-span-3 border rounded-lg overflow-hidden flex flex-col min-h-0">
          <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-700 shrink-0">
            {noneSelected ? 'Disposition — no match' : 'Match details'}
          </div>
          <div className="overflow-y-auto flex-1 p-4 space-y-4">
            {noneSelected ? (
              <>
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="font-semibold text-green-900">No PEP / Sanctions / Adverse Media link</div>
                  <p className="text-sm text-green-800 mt-1">
                    You are recording that none of the returned results should be treated as a hit for this patron.
                  </p>
                </div>
                {savedBanner}
                <div>
                  <label className="text-sm font-medium text-gray-700">Reason (required)</label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {NO_MATCH_PRESETS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setReason(p)}
                        className={`text-xs px-2 py-1 rounded-full border text-left ${
                          reason === p
                            ? 'border-primary-500 bg-primary-50 text-primary-900'
                            : 'border-gray-300 bg-white hover:bg-gray-50'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <textarea
                    className="mt-2 w-full min-h-[100px] rounded-md border border-gray-300 px-3 py-2 text-sm"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Not enough evidence to connect these names to this patron"
                  />
                </div>
                <Button onClick={saveNone} disabled={!canSaveNone}>
                  {saving ? 'Saving…' : isDirty ? 'Save — none of these' : 'Saved'}
                </Button>
                {!checkId && (
                  <p className="text-xs text-amber-700">This check was not saved to history yet — re-run AML to enable review.</p>
                )}
              </>
            ) : selected ? (
              <>
                {savedBanner}
                <div className="border rounded-lg p-4 bg-orange-50/40">
                  <div className="font-semibold text-gray-900 mb-3">Risk assessment</div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
                    <div>
                      <div className="inline-block px-3 py-2 text-sm font-bold rounded bg-orange-500 text-white">
                        {selected.riskLevel || 'Review'}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">Overall risk</div>
                    </div>
                    <div>
                      <div className="inline-block px-3 py-2 text-sm font-medium rounded border bg-purple-50 text-purple-800 border-purple-200">
                        {formatThreatLevel(selected.threatLevel) || '—'}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">Threat</div>
                    </div>
                    <div>
                      <div className={`inline-block px-3 py-2 text-sm font-medium rounded border ${getAmlRiskBadgeColor(selected.riskCategory)}`}>
                        {selected.riskCategory || '—'}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">Category</div>
                    </div>
                  </div>
                </div>

                <div className="border rounded-lg p-4 space-y-2 text-sm">
                  <div className="text-base font-semibold text-gray-900">{getMatchCardHeadline(selected)}</div>
                  {typeof selected.confidence !== 'undefined' && (
                    <div>
                      <span className="text-gray-600">Match confidence:</span>{' '}
                      <span className={`font-semibold ${getAmlConfidenceColor(selected.confidence)}`}>
                        {selected.confidence}%
                      </span>
                    </div>
                  )}
                  {Array.isArray(selected.aliases) && selected.aliases.length > 0 && (
                    <div>
                      <span className="text-gray-600">Aliases:</span> {selected.aliases.join(', ')}
                    </div>
                  )}
                  {Array.isArray(selected.countries) && selected.countries.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selected.countries.map((c, i) => (
                        <span key={i} className="text-xs bg-blue-50 text-blue-800 px-2 py-1 rounded border border-blue-200">{c}</span>
                      ))}
                    </div>
                  )}
                  <AmlMediaSource match={selected} />
                  {Array.isArray(selected.sanctions) && selected.sanctions.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {selected.sanctions.slice(0, 6).map((s, i) => (
                        <div key={i} className="text-xs text-red-700 bg-red-50 px-2 py-1 rounded border border-red-200">{s}</div>
                      ))}
                    </div>
                  )}
                </div>

                {selected.profileNotes && (
                  <div className="border rounded-lg p-4 bg-amber-50">
                    <div className="text-base font-semibold text-gray-900 mb-2">Intelligence profile</div>
                    <div className="text-sm text-amber-900 whitespace-pre-wrap">
                      {String(sanitizeVendorBranding(selected.profileNotes) || '')}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button onClick={saveConfirmed} disabled={!canSaveConfirm}>
                    {saving ? 'Saving…' : isDirty ? 'Confirm this match' : 'Saved'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setNoneSelected(true)
                      setSaveError(null)
                      if (!reason.trim()) setReason(NO_MATCH_PRESETS[0])
                    }}
                  >
                    None of these instead
                  </Button>
                </div>
                {!checkId && (
                  <p className="text-xs text-amber-700">This check was not saved to history yet — re-run AML to enable review.</p>
                )}
              </>
            ) : (
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <X className="h-4 w-4" />
                Select a match on the left, or choose “None of these”.
              </div>
            )}

            {saveError && (
              <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded-md p-3">{saveError}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function NameFitGroup({ title, hint, rows, emptyMessage, alwaysShow = false, noneSelected, selectedKey, onSelect }) {
  const list = rows || []
  if (!list.length && !alwaysShow) return null
  return (
    <div className="space-y-2">
      <div className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title} ({list.length})
        {hint && list.length > 0 ? <span className="ml-1 font-normal normal-case tracking-normal text-gray-400">· {hint}</span> : null}
      </div>
      {list.length === 0 ? (
        <p className="px-1 text-sm text-gray-500">{emptyMessage || 'None'}</p>
      ) : null}
      {list.map(({ m, idx }) => {
        const key = matchKey(m, idx)
        const active = !noneSelected && selectedKey === key
        const bucket = classifyCategoryBucket(m)
        const tabMeta = AML_CATEGORY_TABS.find((t) => t.key === bucket)
        return (
          <div
            key={key}
            className={`w-full text-left rounded-lg border p-3 transition-colors ${
              active
                ? 'border-primary-500 bg-primary-50 shadow-sm'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <button
              type="button"
              onClick={() => onSelect(key)}
              className="w-full text-left"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{getMatchCardHeadline(m)}</div>
                  <div className="text-xs text-gray-500 mt-0.5 truncate">{getMatchCardSubtitle(m)}</div>
                </div>
                {typeof m.confidence === 'number' && (
                  <span className={`text-sm font-medium shrink-0 ${getAmlConfidenceColor(m.confidence)}`}>
                    {m.confidence}%
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <span className={`text-xs px-2 py-0.5 rounded border ${tabMeta?.badgeClass || getAmlRiskBadgeColor(m.riskCategory)}`}>
                  {tabMeta?.label || m.riskCategory || 'Other'}
                </span>
              </div>
            </button>
            {bucket === 'adverseMedia' && (
              <AmlMediaSource match={m} compact />
            )}
          </div>
        )
      })}
    </div>
  )
}

function matchKey(m, idx) {
  return [m?.id, m?.profileId, m?.greenidProfileId, m?.primaryName || m?.name, m?.riskCategory, idx]
    .filter((v) => v != null && String(v).trim() !== '')
    .join('::')
}
